import React, { useState } from "react";
import { TrendingUp, Package, RotateCcw, Trophy } from "lucide-react";
import { Kpi, Section, Empty, IncompleteBadge, Num } from "./ui";
import { money, num, meters } from "./format";
import MerchantPhotoThumb from "../MerchantPhotoThumb";

export const ProductRow = ({ p, i, cols, testid }) => (
  <div className="flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-2 py-3 border-b border-[#EBF4F6] last:border-0 min-w-0" data-testid={testid}>
    <span className="w-7 h-7 rounded-full bg-[#EBF4F6] text-[#09637E] font-extrabold text-sm flex items-center justify-center shrink-0 tabular-nums">{i + 1}</span>
    <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-[#F5FAFB]">{p.exists !== false ? <MerchantPhotoThumb photoId={p.product_id} /> : null}</div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-bold text-[#333333] truncate">{p.display_name || "منتج محذوف"}</div>
      <div className="text-[11px] text-[#777777] font-mono truncate"><bdi dir="ltr">{p.product_code || "—"}{p.size ? ` · ${p.size}` : ""}</bdi></div>
    </div>
    <div className="grid gap-x-3 md:gap-x-4 gap-y-0.5 basis-full md:basis-auto md:shrink-0 min-w-0 grid-cols-[repeat(var(--cols),minmax(0,1fr))] md:grid-cols-[repeat(var(--cols),minmax(0,auto))]" style={{ "--cols": cols.length }} dir="rtl">
      {cols.map(([label, value, cls]) => (
        <div key={label} className="text-right min-w-0">
          <div className="text-[10px] text-[#888888] leading-none truncate">{label}</div>
          <div className={`text-sm md:text-base font-extrabold tabular-nums break-words ${cls || "text-[#333333]"}`}><bdi dir="auto">{value}</bdi></div>
        </div>
      ))}
    </div>
  </div>
);

const profitCell = (p) => (p.profit === null || p.profit === undefined ? ["—", "text-amber-600"] : [money(p.profit), p.profit >= 0 ? "text-emerald-700" : "text-rose-600"]);

export function ProfitTab({ data }) {
  const s = data.summary;
  return (
    <div className="space-y-4 md:space-y-6" data-testid="profit-tab">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4">
        <Kpi big label={s.profit_complete ? "الربح الإجمالي" : "الربح المحسوب (غير مكتمل)"} value={money(s.profit)} tone={s.profit >= 0 ? "green" : "red"} testid="profit-total" sub="على المنتجات ذات التكلفة المعروفة فقط" />
        <Kpi label="الإيراد المحسوب" value={money(s.costed_revenue)} tone="primary" testid="profit-revenue" sub="مبيعات منتجات معلومة التكلفة" />
        <Kpi label="رأس المال المعروف" value={money(s.known_cost)} tone="gray" testid="profit-cost" sub="التكلفة × الكمية المباعة" />
        <Kpi label="أثر المرتجعات على الربح" value={money(s.returns_profit_impact)} tone="amber" testid="profit-returns" />
        <Kpi label="مبيعات بلا تكلفة معروفة" value={money(s.uncosted_value)} tone="amber" testid="profit-uncosted" sub={`${s.uncosted_lines} سطر بيع · ${s.uncosted_products} منتج`} />
      </div>
      {!s.profit_complete && s.uncosted_lines > 0 && <IncompleteBadge count={s.uncosted_products} value={money(s.uncosted_value)} />}
      <Section title="ترتيب المنتجات حسب الربح" icon={TrendingUp} testid="profit-products">
        {data.products.length === 0 ? <Empty text="لا مبيعات ضمن الفترة" testid="profit-empty" /> : data.products.map((p, i) => (
          <ProductRow key={p.product_id} p={p} i={i} testid={`profit-row-${p.product_id}`} cols={[
            ["المباع", meters(p.qty_sold)], ["الإيراد", money(p.revenue), "text-[#09637E]"], ["التكلفة", p.cost_usd ? money(p.known_cost) : "غير محدد", p.cost_usd ? "" : "text-amber-600"], ["الربح", ...profitCell(p)],
          ]} />
        ))}
      </Section>
    </div>
  );
}

const SORTS = [["qty_sold", "الأكثر مبيعاً"], ["revenue", "الأعلى إيراداً"], ["profit", "الأعلى ربحاً"], ["returned_qty", "الأكثر إرجاعاً"]];

export function ProductsTab({ data }) {
  const [sort, setSort] = useState("qty_sold");
  const rows = [...data.products].filter((p) => p.qty_sold > 0 || p.returned_qty > 0).sort((a, b) => (b[sort] ?? -Infinity) - (a[sort] ?? -Infinity));
  const totals = rows.reduce((t, p) => ({ qty: t.qty + p.qty_sold, rev: t.rev + p.revenue, ret: t.ret + p.returned_value }), { qty: 0, rev: 0, ret: 0 });
  return (
    <div className="space-y-4 md:space-y-6" data-testid="products-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        <Kpi label="منتجات بيعت" value={num(rows.filter((p) => p.qty_sold > 0).length)} testid="products-count" />
        <Kpi label="الكمية المباعة" value={meters(totals.qty)} tone="teal" testid="products-qty" />
        <Kpi label="صافي الإيراد" value={money(totals.rev - totals.ret)} tone="green" testid="products-net" className="col-span-2 md:col-span-1" sub={<>إيراد <Num>{money(totals.rev)}</Num> − مرتجع <Num>{money(totals.ret)}</Num></>} />
      </div>
      <Section title="أداء المنتجات" icon={Package} testid="products-ranking" action={
        <div className="flex gap-1 bg-[#EBF4F6] p-1 rounded-lg overflow-x-auto" data-testid="products-sort">
          {SORTS.map(([k, l]) => <button key={k} onClick={() => setSort(k)} data-testid={`products-sort-${k}`} className={`h-8 px-2.5 rounded-md text-xs font-bold whitespace-nowrap ${sort === k ? "bg-[#09637E] text-white" : "text-[#333333]"}`}>{l}</button>)}
        </div>}>
        {rows.length === 0 ? <Empty text="لا حركة منتجات ضمن الفترة" testid="products-empty" /> : rows.map((p, i) => (
          <ProductRow key={p.product_id} p={p} i={i} testid={`product-row-${p.product_id}`} cols={[
            ["المباع", meters(p.qty_sold)], ["الإيراد", money(p.revenue), "text-[#09637E]"], ["الربح", ...profitCell(p)], ["مرتجع", meters(p.returned_qty), p.returned_qty > 0 ? "text-rose-600" : "text-[#999999]"],
          ]} />
        ))}
      </Section>
    </div>
  );
}

export function ReturnsTab({ data }) {
  const s = data.summary;
  return (
    <div className="space-y-4 md:space-y-6" data-testid="returns-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
        <Kpi big label="قيمة المرتجعات" value={money(s.returned_value)} tone="red" testid="returns-value" />
        <Kpi label="عدد عمليات الإرجاع" value={num(s.returns_count)} tone="gray" testid="returns-count" />
        <Kpi label="الكمية المرتجعة" value={meters(s.returned_qty)} tone="amber" testid="returns-qty" />
        <Kpi label="أثرها على الربح" value={money(s.profit_impact)} tone="amber" testid="returns-profit" sub="للمنتجات معلومة التكلفة" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <Section title="المنتجات الأكثر إرجاعاً" icon={RotateCcw} testid="returns-products">
          {data.products.length === 0 ? <Empty text="لا مرتجعات ضمن الفترة" testid="returns-empty" /> : data.products.map((p, i) => (
            <ProductRow key={p.product_id} p={p} i={i} testid={`returns-row-${p.product_id}`} cols={[["مرات", num(p.returns_count)], ["الكمية", meters(p.returned_qty), "text-rose-600"], ["القيمة", money(p.returned_value), "text-rose-600"]]} />
          ))}
        </Section>
        <Section title="من نفّذ الإرجاع" icon={Trophy} testid="returns-actors">
          {data.processed_by.length === 0 ? <Empty text="—" /> : data.processed_by.map((a) => (
            <div key={a.actor_id} className="flex items-center justify-between gap-2 py-2.5 border-b border-[#EBF4F6] last:border-0 min-w-0" data-testid={`returns-actor-${a.actor_id}`}>
              <span className="font-bold text-[#333333] text-sm min-w-0 truncate">{a.name}</span>
              <span className="text-sm tabular-nums shrink-0"><Num className="text-[#333333] font-bold">{a.count}</Num> <span className="text-[#888888]">·</span> <Num className="text-rose-600 font-bold">{money(a.value)}</Num></span>
            </div>
          ))}
          <div className="text-[11px] text-[#888888] mt-3">تنفيذ الإرجاع لا يعني أن الموظف هو من باع — راجع «أداء الموظفين» للتمييز بين مَن باع ومَن رجّع.</div>
        </Section>
      </div>
      {data.recent.length > 0 && (
        <Section title="آخر عمليات الإرجاع" icon={RotateCcw} testid="returns-recent">
          <div className="divide-y divide-[#EBF4F6]">
            {data.recent.map((r) => (
              <div key={r.id} className="py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm min-w-0" data-testid={`return-${r.id}`}>
                <span className="text-[#777777] text-xs tabular-nums">{new Date(r.returned_at).toLocaleDateString("ar-SY")}</span>
                <span className="font-bold text-[#333333] flex-1 min-w-0 truncate">{r.display_name || r.product_code}</span>
                <span className="tabular-nums shrink-0"><Num>{num(r.quantity, 2)} م</Num> · <Num className="text-rose-600 font-bold">{money(r.value)}</Num></span>
                <span className="text-xs text-[#777777]">باع: {r.original_seller?.actor_name || "—"} · رجّع: {r.returned_by?.actor_name || "—"}</span>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
